import type { SitePass } from '@/api/types/ProviderApi'
import { CardRule } from '@/components/VisitPage/CardRule'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { passHint } from '@/features/visits/sitePass'
import { analytics } from '@/lib/analytics'
import { useDiscColor } from '@/ui/core/card-ground'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'
import QRCode from 'react-native-qrcode-svg'

/** Wide enough to scan off a phone held at arm's length, inside the sheet's own margins. */
const QR_SIZE = 196

/**
 * The pass itself, in a sheet the page stays behind.
 *
 * A sheet rather than a screen: a member glances at this, holds the phone up, and drops it. A
 * screen would be somewhere to navigate to and then have to come back from, on the one step of
 * the walk where their hands are full.
 *
 * Drawn on white whatever is around it — a QR on pink is a QR some scanners will not read — and
 * the payload is written underneath, because gates fail and desks ask.
 */
const PassSheet = ({
	pass,
	open,
	onClose,
}: { pass: SitePass; open: boolean; onClose: () => void }) => (
	<Sheet open={open} onClose={onClose}>
		<View className="pt-2">
			{/* No line under it: the code is the sheet's whole content and the row that opened
			    it has already said where to hold it up. */}
			<SheetTitle glyph="qr">{`${pass.name} Pass`}</SheetTitle>
			<VStack gap={16} className="items-center px-4 pb-8">
				<View className="rounded-note bg-white p-3">
					<QRCode value={pass.code} size={QR_SIZE} backgroundColor={colors.white} />
				</View>
				{/* Written out as well as drawn, because a scanner fails and a receptionist asks. */}
				<Text weight="bold" className="tracking-wide">
					{pass.code}
				</Text>
			</VStack>
		</View>
	</Sheet>
)

/**
 * The building's pass, as a row in the visit's card.
 *
 * It sits under the address because that is the fact it belongs to: the address says the studio
 * is inside WeWork, and this is how a member gets through WeWork's door. The card is already a
 * list whose length depends on the visit — the suite line, the arrival guide — so a row that
 * comes and goes is the card's own grammar, where a section of its own would leave a hole in the
 * page at every studio with its own front door, which is most of them.
 *
 * Live from the moment a pass exists, which is whenever the building's email lands. It used to
 * appear only after a member told us they had arrived, which had them walking up to a gate
 * holding the thing that gets them through it behind a button they could not press yet.
 */
export const SitePassRow = ({ pass }: { pass: SitePass }) => {
	const [open, setOpen] = useState(false)
	const disc = useDiscColor()

	return (
		<>
			<Pressable
				onPress={() => {
					analytics.trackClick('site-pass-row')
					setOpen(true)
				}}
				accessibilityRole="button"
				accessibilityLabel={`${pass.name} Pass. ${passHint(pass)}`}
				className="flex-row items-center px-4 py-4"
				style={{ gap: 12 }}
			>
				{/* The card's rows are one blush block divided by hairlines, not alternating
				    grounds: the pass stood on white to separate itself, which made it the one row
				    that looked like a different kind of thing. It is the same kind of thing — a
				    fact about the building, one tap from what it opens. */}
				<CardRule />
				<View
					className="h-10 w-10 items-center justify-center rounded-full"
					style={{ backgroundColor: disc }}
				>
					{/* The code itself, which is what is behind the row. The first version was a
					    real QR's density shrunk to a disc; this one is drawn at the size it
					    renders. The padlock it replaced said what the pass is for rather than
					    what it is — and it is the mark the profile already uses for Face ID. */}
					<Glyph name="qr" size={19} color={colors.ink} />
				</View>
				<VStack gap={2} className="flex-1">
					<Text size="body0" weight="bold">
						{`${pass.name} Pass`}
					</Text>
					{/* Where to use it, which is the one thing a member cannot work out from a QR
					    code. The row lost this line for a day on the argument that a chevron
					    already says there is something to hold up — but it does not say *where*,
					    and standing in a lobby holding the right code at the wrong desk is the
					    failure this row exists to prevent. */}
					<Text size="body2" tone="muted">
						{passHint(pass)}
					</Text>
				</VStack>
				<Glyph name="chevron" size={14} color={colors.ink} />
			</Pressable>
			<PassSheet pass={pass} open={open} onClose={() => setOpen(false)} />
		</>
	)
}
